import { ApolloClient, createHttpLink, fromPromise, InMemoryCache } from "@apollo/client"
import type { ContextSetter } from "@apollo/client/link/context"
import { setContext } from "@apollo/client/link/context"
import { onError } from "@apollo/client/link/error"
import { RetryLink } from "@apollo/client/link/retry"
import { getApps } from "firebase/app"
import { getAuth, getIdToken } from "firebase/auth"
import { Config } from "interface/config"

// サーバー側では最小限のキャッシュ、クライアント側では通常キャッシュ
const isBrowser = typeof window !== 'undefined'

// Firebase Authの初期化状態を追跡
let isFirebaseAuthInitialized = false
let firebaseAuthInitPromise: Promise<void> | null = null

// Firebase Auth初期化を待機するためのPromiseを作成
// Firebase SDK 12.x で追加された authStateReady() を使用
const waitForFirebaseAuth = (): Promise<void> => {
  if (isFirebaseAuthInitialized) {
    return Promise.resolve()
  }

  if (firebaseAuthInitPromise) {
    return firebaseAuthInitPromise
  }

  firebaseAuthInitPromise = new Promise((resolve) => {
    // Firebaseアプリが初期化されるまで待つ
    const checkFirebase = () => {
      if (getApps().length > 0) {
        // Firebase SDK 12.x の authStateReady() を使用
        // これは初期認証状態が確定するまで待機する公式メソッド
        getAuth().authStateReady().then(() => {
          isFirebaseAuthInitialized = true
          resolve()
        })
      } else {
        // Firebaseアプリがまだ初期化されていない場合、少し待ってリトライ
        setTimeout(checkFirebase, 50)
      }
    }
    checkFirebase()
  })

  return firebaseAuthInitPromise
}

// ブラウザ環境でのみ、Firebase Auth初期化完了を監視
if (isBrowser) {
  // 即座に初期化を開始
  waitForFirebaseAuth()
}

const httpLink = createHttpLink({
  uri: Config.graphqlEndpoint,
  credentials: "same-origin",
})

// トークン取得のリトライ設定
const MAX_TOKEN_RETRIES = 2
const TOKEN_RETRY_DELAY_MS = 1000

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

const contextSetter: ContextSetter = async (_, context) => {
  // ブラウザ環境では、Firebase Auth初期化を待機
  if (isBrowser) {
    await waitForFirebaseAuth()
  }

  for (let attempt = 0; attempt < MAX_TOKEN_RETRIES; attempt++) {
    try {
      if (getApps().length === 0) {
        return {
          headers: {
            ...context.headers,
            token: null,
          },
        }
      }
      const currentUser = getAuth().currentUser
      if (currentUser === null) {
        return {
          headers: {
            ...context.headers,
            token: null,
          },
        }
      }
      // Get ID token (Firebase automatically refreshes if expired)
      // リトライ時は強制リフレッシュを実行
      const forceRefresh = attempt > 0
      const token = await getIdToken(currentUser, forceRefresh)
      return {
        headers: {
          ...context.headers,
          token: token,
        },
      }
    } catch (error) {
      // 最後のリトライでも失敗した場合のみログ出力
      if (attempt === MAX_TOKEN_RETRIES - 1) {
        console.error("Failed to get authentication token after retries:", error)
        return { headers: { ...context.headers, token: null } }
      }
      // リトライ前に待機
      await sleep(TOKEN_RETRY_DELAY_MS)
    }
  }
  return { headers: { ...context.headers, token: null } }
}

const authLink = setContext(contextSetter)

// セッション切れとしてログインページへ誘導する
const redirectToLogin = () => {
  // Firebase Authが初期化されていない場合はリダイレクトしない
  // （初期化中にUNAUTHORIZEDが返されることがあるため）
  if (!isFirebaseAuthInitialized) {
    return
  }

  // 既にルートページにいる場合はリダイレクトしない（ループ防止）
  if (window.location.pathname === '/' || window.location.pathname === '/ja') {
    return
  }

  // セッション切れフラグを設定（ログインページで通知表示用）
  sessionStorage.setItem('sessionExpired', 'true')
  // ログインページへリダイレクト
  window.location.href = '/'
}

const hasUnauthorizedError = (errors: readonly { extensions?: Record<string, unknown> }[] | undefined) =>
  (errors ?? []).some((err) => err.extensions?.code === 'UNAUTHORIZED')

// UNAUTHORIZEDエラー時のグローバルハンドリング
export const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    // UNAUTHORIZEDエラーの場合（ブラウザ環境でのみリカバリ処理）
    if (hasUnauthorizedError(graphQLErrors) && isBrowser) {
      // スリープ復帰直後などで期限切れトークンを送ってしまった場合に備え、
      // ログイン中であればトークンを強制リフレッシュして1回だけ再試行する
      const currentUser = getApps().length > 0 ? getAuth().currentUser : null
      if (!operation.getContext().unauthorizedRetried && currentUser !== null) {
        return fromPromise(
          getIdToken(currentUser, true).catch(() => null),
        )
          .flatMap((token) => {
            operation.setContext((previousContext: Record<string, unknown>) => ({
              ...previousContext,
              unauthorizedRetried: true,
              headers: {
                ...(previousContext.headers as Record<string, unknown> | undefined),
                token,
              },
            }))
            return forward(operation)
          })
          .map((result) => {
            // onErrorは自分が返した再試行結果のエラーを再処理しないため、ここで判定する
            if (hasUnauthorizedError(result.errors)) {
              redirectToLogin()
            }
            return result
          })
      }

      redirectToLogin()
      return
    }
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError}`)
  }
})

// ネットワークエラー時のリトライ設定（502, 503, 504 等に対応）
const retryLink = new RetryLink({
  delay: {
    initial: 300,     // 初回リトライまで300ms待機
    max: 2000,        // 最大2秒まで待機
    jitter: true      // ランダム化してサーバー負荷を分散
  },
  attempts: {
    max: 3,           // 最大3回リトライ
    retryIf: (error) => {
      // ネットワークエラーまたはサーバーエラー(5xx)の場合にリトライ
      if (!error) return false

      // ServerError の場合は statusCode をチェック
      const statusCode = (error as { statusCode?: number }).statusCode
      if (statusCode && statusCode >= 500) {
        console.warn(`[RetryLink] Retrying due to server error: ${statusCode}`)
        return true
      }

      // ネットワーク接続エラーの場合もリトライ
      if (error.message?.includes('Failed to fetch') ||
          error.message?.includes('Network request failed') ||
          error.message?.includes('Load failed')) {
        console.warn(`[RetryLink] Retrying due to network error: ${error.message}`)
        return true
      }

      return false
    }
  }
})

const cache = isBrowser
  ? new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            // Viewer型にIDがないため、異なるクエリ結果を正しくマージするための設定
            viewer: {
              merge(existing, incoming, { mergeObjects }) {
                return mergeObjects(existing, incoming)
              },
            },
          },
        },
      },
    })
  : new InMemoryCache({
      resultCaching: false,
      typePolicies: {},
    })

export const client = new ApolloClient({
  ssrMode: !isBrowser,
  devtools: { enabled: false }, // DevTools無効化でメモリ削減
  link: authLink.concat(retryLink).concat(errorLink).concat(httpLink),
  cache: cache,
  // サーバー側での追加最適化
  defaultOptions: {
    watchQuery: {
      fetchPolicy: isBrowser ? 'cache-and-network' : 'network-only',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: isBrowser ? 'cache-first' : 'network-only',
      errorPolicy: 'all',
    },
  },
})
